import { swapIcon } from "../../../common/js/icon";

/**
 * The behaviour of the share dialog (singleimage/share_body.php; concepts/sharing.md): one
 * delegated listener on the document serves every share dialog, whoever opened it - a
 * thumbnail, the lightbox, the single image page, the ajax layout. The dialog itself carries
 * no script, so it works under a Content Security Policy without 'unsafe-inline' or
 * 'unsafe-eval'; until 7.0.0 it brought two inline scripts and onclick handlers which Overlay
 * ran through eval().
 *
 * - The copy button (#eg-copy-link-btn) puts the link of #eg-share-link-input on the
 *   clipboard and says so in words in the status line (a live region) and with a tick on the
 *   button; when the browser refuses, the link is selected in its field and the status line
 *   asks the visitor to copy it. Until 7.0.0 the button only turned green, also when the copy
 *   had failed.
 * - A network tile (a[data-eg-share-popup]) opens its share page in a small window for a plain
 *   click; a click with a modifier, the middle button or a blocked window keeps the link's own
 *   way, a new tab.
 * - The share sheet of the device (navigator.share: the messengers, the mail and the other
 *   apps of a phone) is the first target where the browser offers it: the tile
 *   [data-eg-share-native] comes hidden and is shown once the dialog is in the page. Until
 *   7.0.0 a phone offered four desktop networks and nothing else (SHR-9).
 * - The field of the link selects the whole link when it gets the focus.
 */

const COPY_BUTTON = '#eg-copy-link-btn';
const NATIVE_ITEM = '[data-eg-share-native]';
const NATIVE_BUTTON = '[data-eg-share-native-button]';
const LINK_INPUT = '#eg-share-link-input';
const STATUS = '.eg-share__status';
const POPUP_LINK = 'a[data-eg-share-popup]';
const POPUP_FEATURES = 'width=626,height=560,menubar=no,toolbar=no,resizable=yes,scrollbars=yes';
const FEEDBACK_MS = 2000;

/**
 * Writes the text to the clipboard: the Clipboard API, else the copy command of the
 * document on the selected field. Resolves with whether the browser took it.
 */
export async function writeToClipboard(text: string, field: HTMLInputElement | null): Promise<boolean> {
    try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (e) {
        // refused (no permission, an insecure page): try the copy command
    }

    if (!field) {
        return false;
    }
    // the copy command copies the selection, which needs the focus in the field; it goes back
    // to the button afterwards, so the tab key goes on from where the visitor was
    const previous = document.activeElement as HTMLElement | null;
    field.focus();
    field.select();
    field.setSelectionRange(0, field.value.length);
    let copied = false;
    try {
        copied = document.execCommand('copy');
    } catch (e) {
        copied = false;
    }
    if (previous && previous !== field && typeof previous.focus === 'function') {
        previous.focus();
    }
    return copied;
}

/**
 * Says in the status line what happened. The text is set in the next task, so a screen reader
 * announces it again when the same text comes twice.
 */
function announce(status: HTMLElement | null, text: string, failed: boolean): void {
    if (!status) {
        return;
    }
    status.textContent = '';
    status.classList.toggle('is-failed', failed);
    window.setTimeout(() => {
        status.textContent = text;
    }, 50);
}

/**
 * Copies the link of the dialog the button belongs to and shows the outcome.
 */
export async function copyLink(button: HTMLElement): Promise<boolean> {
    const root: ParentNode = button.closest('.eg-share') || button.closest('#eventgallery-overlay') || document;
    const field = root.querySelector(LINK_INPUT) as HTMLInputElement | null;
    const status = root.querySelector(STATUS) as HTMLElement | null;
    if (!field) {
        return false;
    }

    const copied = await writeToClipboard(field.value, field);

    if (copied) {
        button.classList.add('is-copied');
        swapIcon(button.querySelector('svg.eg-icon'), 'check');
        announce(status, status ? status.getAttribute('data-copied') || '' : '', false);
        window.setTimeout(() => {
            button.classList.remove('is-copied');
            swapIcon(button.querySelector('svg.eg-icon'), 'copy');
        }, FEEDBACK_MS);
    } else {
        field.focus();
        field.select();
        announce(status, status ? status.getAttribute('data-failed') || '' : '', true);
    }

    return copied;
}

/**
 * Opens the share page of a network in a small window. Returns whether it did; the caller
 * keeps the link's own way (a new tab) when it did not.
 */
export function openPopup(event: MouseEvent, link: HTMLAnchorElement): boolean {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return false;
    }

    const popup = window.open(link.href, 'eg-share', POPUP_FEATURES);
    if (!popup) {
        return false;
    }
    // the page of the network gets no handle on ours (what rel="noopener" does for the link)
    try {
        popup.opener = null;
    } catch (e) {
        // a window which forbids it is fine as well
    }
    event.preventDefault();
    return true;
}

/**
 * The data of the share sheet, if the browser has one and takes it.
 */
function shareData(button: Element): ShareData {
    return {
        title: button.getAttribute('data-title') || undefined,
        url: button.getAttribute('data-url') || '',
    };
}

function canShareNatively(button: Element): boolean {
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
        return false;
    }
    if (typeof navigator.canShare === 'function') {
        try {
            return navigator.canShare(shareData(button));
        } catch (e) {
            return false;
        }
    }
    return true;
}

/**
 * Shows the tile of the share sheet in the dialogs under the root where the browser has one.
 */
export function prepareNativeShare(root: ParentNode = document): void {
    root.querySelectorAll(NATIVE_ITEM).forEach((item) => {
        const button = item.querySelector(NATIVE_BUTTON);
        if (button && canShareNatively(button)) {
            item.removeAttribute('hidden');
        }
    });
}

/**
 * Opens the share sheet of the device with the link of the dialog. Closing the sheet without
 * picking an app rejects with an AbortError, which is no error; the dialog stays open either
 * way, with the copy and the networks.
 */
export async function shareNatively(button: Element): Promise<boolean> {
    if (typeof navigator.share !== 'function') {
        return false;
    }
    try {
        await navigator.share(shareData(button));
        return true;
    } catch (e) {
        return false;
    }
}

function onImagesAdded(event: Event): void {
    const detail = (event as CustomEvent).detail;
    if (detail && detail.isOverlay) {
        prepareNativeShare(document);
    }
}

function onClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    if (!target || typeof target.closest !== 'function') {
        return;
    }

    const native = target.closest(NATIVE_BUTTON);
    if (native) {
        event.preventDefault();
        void shareNatively(native);
        return;
    }

    const copy = target.closest(COPY_BUTTON) as HTMLElement | null;
    if (copy) {
        event.preventDefault();
        void copyLink(copy);
        return;
    }

    const popupLink = target.closest(POPUP_LINK) as HTMLAnchorElement | null;
    if (popupLink) {
        openPopup(event, popupLink);
    }
}

function onFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLInputElement | null;
    if (target && typeof target.matches === 'function' && target.matches(LINK_INPUT)) {
        target.select();
    }
}

let bound: Document | null = null;

/**
 * Binds the share dialogs of the document, once.
 */
export function bindShareDialog(doc: Document = document): void {
    if (bound === doc) {
        return;
    }
    bound = doc;
    doc.addEventListener('click', onClick);
    doc.addEventListener('focusin', onFocusIn);
    // Overlay.js says so once the content of a dialog is in the page
    doc.addEventListener('eventgallery-images-added', onImagesAdded);
}

/**
 * Takes the listeners away again (the specs).
 */
export function unbindShareDialog(doc: Document = document): void {
    doc.removeEventListener('click', onClick);
    doc.removeEventListener('focusin', onFocusIn);
    doc.removeEventListener('eventgallery-images-added', onImagesAdded);
    if (bound === doc) {
        bound = null;
    }
}
